import {
  IClinicServiceDetailsMapping,
  IConfigurationStaffOnboardPostBody,
} from "@/api_framework/api_modals/Configuration";
import { PRIMARY_BUTTON, SECONDARY_BUTTON } from "@/constants";
import FadeIn from "@/app/components/ui/FadeIn";
import Input from "@/app/components/ui/Inputs/Input";
import RadioGroupComponent from "@/app/components/ui/RadioGroup";
import SearchableMultiSelectMenu from "@/app/components/ui/SelectMenus/MultiSelectMenu";
import NonSearchableSelectMenu from "@/app/components/ui/SelectMenus/NonSearchableSelectMenu";
import SelectMenuWithSubOptions from "@/app/components/ui/SelectMenus/SelectMenuWithSubOptions";
import Toggle from "@/app/components/ui/Toggles";
import { ISelectMenuItemData } from "@/app/components/ui/interface";
import { useConfigurationCityListAPI } from "@/app/hooks/api_hooks/configuration/useConfigurationCityListAPI";
import { useConfigurationProviderCategoryListAPI } from "@/app/hooks/api_hooks/configuration/useConfigurationProviderCategoryListAPI";
import { useConfigurationRBACUserRolesAndPermissionListAPI } from "@/app/hooks/api_hooks/configuration/useConfigurationRBACUserRolesAndPermissionListAPI";
import { useConfigurationStaffClinicBasedOnCityAPI } from "@/app/hooks/api_hooks/configuration/useConfigurationStaffClinicBasedOnCityAPI";
import { useConfigurationStaffDetailAPI } from "@/app/hooks/api_hooks/configuration/useConfigurationStaffDetailAPI";
import { useConfigurationStaffOnboardAPI } from "@/app/hooks/api_hooks/configuration/useConfigurationStaffOnboardAPI";
import { useConfigurationStaffUpdateAPI } from "@/app/hooks/api_hooks/configuration/useConfigurationStaffUpdateAPI";
import { useOrgServicesByFilterAPI } from "@/app/hooks/api_hooks/org/useOrgServicesByFilterAPI";
import useFormValidations from "@/app/hooks/useFormValidations";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useRouter } from "@/app/hooks/useRouter";
import { useSelectMenuReducer } from "@/app/hooks/useSelectMenuReducer";
import { IConfigurationListState } from "@/app_redux/reducers/slices/auth/configuration_slice";
import { useFeatureAccess } from "@/app/hooks/useFeatureAccess";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router";
import { useSearchParams } from "react-router-dom";

type StaffRoleStatusTypes = "active" | "inactive";
export interface IStaffOnboardFormFields {
  staffName: string;
  staffDisplayName: string;
  mobile: string;
  email: string;
  cities: Array<string>;
  clinics: Array<string>;
  categoryId: string;
  categoryName: string;
  staffRoleId: string;
  staffRoleName: string;
  staffRoleStatus: StaffRoleStatusTypes;
  expertise?: string;
  experience?: string;
  image_url?: string;
  designation?: string;
  specialities?: string;
}

export type IClinicServiceMapping = {
  [key: string]: { [key: string]: ISelectMenuItemData[] };
};

const ConfigurationStaffOnboard = () => {
  let categoryDetailFetched = false;
  let allFetched = false;
  const [isForEdit, setIsForEdit] = useState<boolean>(false);
  const navigate = useNavigate();
  const { forStaffName } = useFormValidations();
  const [params, _setParams] = useSearchParams();
  const [isServiceMappingOn, setIsServiceMappingOn] = useState(false);
  const { getRouteKey } = useRouter();
  const { forMobile, forEmail } = useFormValidations();
  const [isSubmit, setIsSubmit] = useState(false);
  const [isDoctor, setIsDoctor] = useState(false);

  const [clinicServiceDetails, setClinicServiceDetails] = useState<IClinicServiceDetailsMapping[]>([]);
  const formHook = useForm<IStaffOnboardFormFields>({
    mode: "onChange",
    defaultValues: {
      staffRoleStatus: "active",
    },
  });
  const { staffDetails, execute: fetchStaffDetails } = useConfigurationStaffDetailAPI();
  const { newStaffId, execute: onboardStaff } = useConfigurationStaffOnboardAPI();
  const { updatedStaffId, execute: updateStaff } = useConfigurationStaffUpdateAPI();
  const { serviceTypes: staffSetupServiceTypes, execute: fetchOrgServicesByFilter } = useOrgServicesByFilterAPI();
  const fetchCities = useConfigurationCityListAPI();
  const { execute: fetchStaffRolesList } = useConfigurationRBACUserRolesAndPermissionListAPI();
  const fetchStaffCategory = useConfigurationProviderCategoryListAPI();
  const { clinicsByCities, execute: fetchStaffClinicBasedOnCity } = useConfigurationStaffClinicBasedOnCityAPI();
  const { routeState } = useRouteInfo(getRouteKey("CONFIGURATION", "id"));
  const { staffRolesList, providerCategoryList, cityList } = routeState?.state as IConfigurationListState;
  // RBAC: STAFF_ONBOARD_UNRESTRICTED_ROLE_LIST — legacy role: super_admin
  const canSeeAllStaffRoles = useFeatureAccess("STAFF_ONBOARD_UNRESTRICTED_ROLE_LIST");
  const ALLOWED_NON_SUPER_ADMIN_ROLE_IDS = [
    "eb6b7abe-06ee-4abf-85fc-88073e8221c4",
    "afbf4e94-2e1d-49d4-9b2d-13c2309f9e86",
    "e6d76c17-80ca-4f8c-8137-31143eb725b3",
    "91e54ddc-7f42-461f-b2f5-9372eef9d000",
    "c301a5d0-34e5-4b82-8d8a-484b106ce0c6"
  ];
  const filteredStaffRolesList =
    canSeeAllStaffRoles
      ? staffRolesList
      : staffRolesList?.filter((role) => ALLOWED_NON_SUPER_ADMIN_ROLE_IDS.includes(role?.role_id));
  const staffRolesItem = useSelectMenuReducer(filteredStaffRolesList, "role_name", "role_id");
  const staffProviderCategoryItem = useSelectMenuReducer(
    providerCategoryList,
    "staff_category_name",
    "staff_category_id",
  );
  const cityOrgItem = useSelectMenuReducer(cityList, "name", "id");
  const clinicsOrgItem = useSelectMenuReducer(clinicsByCities, "clinic_name", "clinic_id");
  const [eqpItems, setEqpItems] = useState<ISelectMenuItemData[]>([]);

  const getServiceIdBySubServiceId = (subServiceId: string) => {
    const matchedSubService = eqpItems
      .flatMap((serviceType) => serviceType?.subSelectItems || [])
      .find((subService) => subService?.id === subServiceId);
    return matchedSubService?.state?.serviceId;
  };

  const getServiceTypeIdByServiceId = (serviceId: string) => {
    return (
      (staffSetupServiceTypes ?? []).find((serviceType) =>
        (serviceType?.services ?? []).some((service) => service?.service_id === serviceId),
      )?.id ?? ""
    );
  };

  const getValidSubServiceIdsForServices = (serviceIds: string[], subServiceIds: string[]) => {
    const allowedSubServiceIds = new Set(
      eqpItems
        .filter((service) => serviceIds.includes(service?.id))
        .flatMap((service) => service?.subSelectItems?.map((subService) => subService?.id) || []),
    );
    return [...new Set(subServiceIds.filter((subServiceId) => allowedSubServiceIds.has(subServiceId)))];
  };

  const normalizeClinicServiceDetails = (clinicDetails: IClinicServiceDetailsMapping[]) => {
    return clinicDetails.map((clinic) => {
      const uniqueSubServiceIds = [...new Set(clinic?.sub_service_ids || [])];
      const serviceIdsFromSelectedSubServices = [
        ...new Set(uniqueSubServiceIds.map((subServiceId) => getServiceIdBySubServiceId(subServiceId)).filter(Boolean)),
      ] as string[];

      return {
        ...clinic,
        service_ids: serviceIdsFromSelectedSubServices,
        sub_service_ids: uniqueSubServiceIds,
      };
    });
  };

  useEffect(() => {
    const serviceItems = (staffSetupServiceTypes ?? []).reduce((final, serviceType) => {
      if (!serviceType?.id) return final;
      const services = serviceType?.services ?? [];
      const subServiceItems = services.flatMap((service) =>
        (service?.sub_services ?? [])
          .filter((subService) => Boolean(subService?.sub_service_id))
          .map((subService) => ({
            id: subService.sub_service_id,
            title: subService?.sub_service_name || "Sub Service",
            state: { serviceId: service?.service_id || "" },
          })),
      );

      if (!subServiceItems.length) return final;

      final.push({
        id: serviceType.id,
        title: serviceType?.name || "Service Type",
        checkboxVisible: true,
        withSubItems: true,
        subSelectItems: subServiceItems as ISelectMenuItemData[],
      });

      return final;
    }, [] as ISelectMenuItemData[]);

    setEqpItems(serviceItems);
  }, [staffSetupServiceTypes]);

  // console.log(eqpItems, "eqpItems");

  const constructOnboardPayload = (data: IStaffOnboardFormFields): IConfigurationStaffOnboardPostBody => {
    return {
      staff_name: data?.staffName,
      category_details: { category_id: `${data?.categoryId}`, category_name: data?.categoryName },
      city_details: data?.cities,
      clinic_details: data?.clinics,
      email_id: data?.email,
      mobile: data?.mobile,
      role_details: { role_id: `${data?.staffRoleId}`, role_name: data?.staffRoleName },
      status: data?.staffRoleStatus,
      staff_display_name: data?.staffDisplayName,
      service_mapping: isServiceMappingOn,
      expertise: data?.expertise,
      experience: data?.experience,
      image_url: data?.image_url,
      designation: data?.designation,
      specialities: data?.specialities,
    };
  };

  const onSubmit = (data: IStaffOnboardFormFields) => {
    setIsSubmit(true);
    const normalizedClinicServiceDetails = normalizeClinicServiceDetails(clinicServiceDetails);
    if (
      isServiceMappingOn &&
      normalizedClinicServiceDetails?.some((clinic) => clinic?.service_ids?.length == 0 || clinic?.sub_service_ids?.length == 0)
    ) {
      return;
    }
    if (data) {
      let payload = constructOnboardPayload(data);
      if (isServiceMappingOn) {
        payload = {
          ...payload,
          clinic_service_details: normalizedClinicServiceDetails?.map((c) => ({
            ...c,
            service_ids: [...new Set(c?.service_ids)],
            sub_service_ids: [...new Set(c?.sub_service_ids)],
          })),
        };
      }
      if (!isForEdit) {
        onboardStaff(payload);
      } else {
        updateStaff({ ...payload, staff_id: params.get("staff_id") });
      }
    }
  };

  const clearError = (fieldName) => {
    formHook.clearErrors(fieldName);
  };

  useEffect(() => {
    if (newStaffId || updatedStaffId) {
      navigate(getRouteKey("STAFF_SETUP", "url"));
    }
  }, [newStaffId, updatedStaffId]);

  useEffect(() => {
    const cities = formHook?.getValues("cities");
    if (cities?.length >= 0) {
      fetchStaffClinicBasedOnCity(cities);
    }
  }, [formHook.watch("cities")]);

  useEffect(() => {
    if (!allFetched) {
      fetchCities();
      fetchStaffCategory();
      fetchStaffRolesList();
      fetchOrgServicesByFilter({ filterType: "staff_setup" });
      allFetched = true;
    }
  }, []);

  useEffect(() => {
    if (staffDetails) {
      formHook.setValue("staffName", staffDetails?.staff_data?.name);
      formHook.setValue("expertise", staffDetails?.staff_data?.expertise);
      formHook.setValue("experience", staffDetails?.staff_data?.experience);
      formHook.setValue("image_url", staffDetails?.staff_data?.image_url);
      formHook.setValue("designation", staffDetails?.staff_data?.designation);
      formHook.setValue("specialities", staffDetails?.staff_data?.specialities);
      formHook.setValue("staffDisplayName", staffDetails?.staff_data?.display_name);
      formHook.setValue("mobile", staffDetails?.staff_data?.mobile);
      formHook.setValue("email", staffDetails?.staff_data?.email_id);
      formHook.setValue("staffRoleName", staffDetails?.staff_roles_data?.role);
      formHook.setValue("staffRoleId", staffDetails?.staff_roles_data?.role_id);
      setIsServiceMappingOn(staffDetails?.service_mapping);
      formHook.setValue("categoryId", staffDetails?.staff_data?.category_id);
      formHook.setValue("categoryName", staffDetails?.staff_data?.category_name);
      formHook.setValue(
        "staffRoleStatus",
        staffDetails?.staff_data?.status === "true" ? "active" : ("inactive" as "active" | "inactive"),
      );
      formHook.setValue(
        "cities",
        staffDetails?.city_details?.map((c) => c?.city_id),
      );
      formHook.setValue(
        "clinics",
        staffDetails?.clinic_details?.map((c) => c?.clinic_id),
      );
      if (staffDetails?.clinic_service_details?.length > 0) {
        setClinicServiceDetails(staffDetails?.clinic_service_details);
      } else {
        setClinicServiceDetails(
          formHook.watch("clinics")?.map((c) => ({ clinic_id: c, sub_service_ids: [], service_ids: [] })),
        );
      }
    }
  }, [staffDetails]);

  useEffect(() => {
    const staff_role = formHook.watch("staffRoleName");
    if (staff_role?.toLowerCase() === "doctor") {
      setIsDoctor(true);
    } else {
      setIsDoctor(false);
    }
  }, [formHook.watch("staffRoleName")]);

  useEffect(() => {
    if (params.get("staff_id") && !categoryDetailFetched) {
      fetchStaffDetails(params.get("staff_id"));
      setIsForEdit(true);
      categoryDetailFetched = true;
    }
  }, [params.get("staff_id")]);

  const handleCancel = () => {
    formHook.reset();
    navigate(getRouteKey("STAFF_SETUP", "url"));
  };

  useEffect(() => {
    if (clinicsByCities) {
      formHook.setValue(
        "clinics",
        formHook.watch("clinics")?.filter((c) => clinicsOrgItem?.some((o) => o?.id == c)),
      );
      setClinicServiceDetails((prev) => {
        return formHook.watch("clinics")?.map((c) => prev?.find((p) => p?.clinic_id == c));
      });
    }
  }, [clinicsByCities]);

  const handleClinicMultiSelect = (items: ISelectMenuItemData[]) => {
    formHook.setValue(
      "clinics",
      items?.map((i) => i?.id),
    );
    setClinicServiceDetails((prev) =>
      items?.map((c) => {
        const clinic = prev?.find((p) => p?.clinic_id == c?.id);
        return {
          clinic_id: c?.id,
          service_ids: clinic?.service_ids || [],
          sub_service_ids: clinic?.sub_service_ids || [],
        };
      }),
    );
    clearError("clinics");
  };

  const getDefaultParentIds = (clinicId: string) => {
    const serviceIds = clinicServiceDetails?.filter((d) => d?.clinic_id === clinicId)?.[0]?.service_ids || [];
    return [...new Set(serviceIds.map((serviceId) => getServiceTypeIdByServiceId(serviceId)).filter(Boolean))];
  };

  const getDefaultChildIds = (clinicId: string) => {
    return clinicServiceDetails?.filter((d) => d?.clinic_id === clinicId)?.[0]?.sub_service_ids;
  };

  const getClinicName = (clinicId: string) => {
    return clinicsOrgItem?.filter((i) => i?.id === clinicId)?.[0]?.title;
  };

  const getEmptyServices = (clinicId) => {
    const clinic = clinicServiceDetails?.find((c) => c?.clinic_id == clinicId);
    const selectedServices = clinic?.service_ids;
    const result = [];
    selectedServices?.forEach((service) => {
      const serviceItem = eqpItems?.find((item) => item?.id === service);
      if (serviceItem) {
        if (clinic) {
          const subServiceIds = clinic?.sub_service_ids;
          const serviceSubIds = serviceItem?.subSelectItems?.map((subItem) => subItem?.id);
          if (!subServiceIds?.some((s) => serviceSubIds?.some((ss) => ss == s))) {
            result?.push(serviceItem?.title);
          }
        }
      }
    });
    return result;
  };

  const handelImageRedirect = () => {
    window.open("https://vms.vetic.in/app/whitelist-mobile/image-generation", "blank");
  };

  return (
    <div className="bg-white py-3 rounded-md">
      <div className="text-black text-base font-semibold mt-5 px-6">Staff Setup</div>
      <div className="mx-5 mb-24">
        <form
          onSubmit={formHook.handleSubmit(
            (data) => {
              onSubmit(data);
            },
            (_errors) => {
              setIsSubmit(true);
            },
          )}
        >
          <div className="justify-start items-baseline flex mt-5">
            <div className="h-[38px] p-2.5 justify-start w-[250px] items-center gap-1 flex">
              <span className="text-black text-xs font-normal">Staff Name</span>
              <span className="text-rose-600 text-xs font-normal">*</span>
            </div>
            <Input
              register={formHook.register("staffName", {
                ...forStaffName.validations,
                required: true,
              })}
              errorMessages={[forStaffName.errors, { type: "required", message: "Staff Name is required" }]}
              className="h-9 w-[300px] text-xs rounded-md border-slate-400"
              fieldError={formHook.formState.errors?.staffName}
              placeholder="Enter Staff Name"
            />
          </div>
          <div className="justify-start items-baseline flex mt-5">
            <div className="h-[38px] p-2.5 justify-start w-[250px] items-center gap-1 flex">
              <span className="text-black text-xs font-normal">Staff Display Name</span>
              <span className="text-rose-600 text-xs font-normal">*</span>
            </div>
            <Input
              register={formHook.register("staffDisplayName", {
                ...forStaffName.validations,
                required: true,
              })}
              errorMessages={[forStaffName.errors, { type: "required", message: "Staff Display Name is required" }]}
              className="h-9 w-[300px] text-xs rounded-md border-slate-400"
              fieldError={formHook.formState.errors.staffDisplayName}
              placeholder="Enter Staff Display Name"
            />
          </div>
          <div className="justify-start items-baseline flex mt-5">
            <div className="h-[38px] p-2.5 justify-start w-[250px] items-center gap-1 flex">
              <span className="text-black text-xs font-normal">Contact Number</span>
              <span className="text-rose-600 text-xs font-normal">*</span>
            </div>
            <Input
              maxLength={10}
              className="h-9 w-[300px] text-xs rounded-md border-slate-400"
              placeholder="Enter mobile"
              register={formHook.register("mobile", {
                ...forMobile.validations,
                maxLength: 10,
                required: true,
              })}
              fieldError={formHook.formState.errors.mobile}
              errorMessages={[
                forMobile.errors,
                { type: "required", message: "Contact number is required" },
                { type: "maxLength", message: "Invalid contact number" },
              ]}
            />
            {formHook.formState.errors?.mobile && (
              <div className="text-red-500 text-xs mt-1">{formHook.formState.errors.mobile.message}</div>
            )}
          </div>
          <div className="justify-start items-baseline flex mt-5">
            <div className="h-[38px] p-2.5 justify-start w-[250px] items-center gap-1 flex">
              <span className="text-black text-xs font-normal">Email Id</span>
              <span className="text-rose-600 text-xs font-normal">*</span>
            </div>
            <Input
              register={formHook.register("email", {
                ...forEmail.validations,
                required: true,
                maxLength: 100,
              })}
              errorMessages={[
                forEmail.errors,
                { type: "required", message: "Email ID is not required" },
                { type: "maxLength", message: "Email too long." },
              ]}
              className="h-9 text-xs w-[300px] rounded-md border-slate-400"
              fieldError={formHook.formState.errors.email}
              placeholder="Enter Email Id"
            />
            {formHook.formState.errors?.email && (
              <div className="text-red-500 text-xs mt-1">{formHook.formState.errors.email.message}</div>
            )}
          </div>
          <div className="justify-start items-center flex mt-8">
            <div className="h-[38px] p-2.5 justify-start w-[250px] items-center gap-1 flex">
              <span className="text-black text-xs font-normal">Choose Category</span>
              <span className="text-rose-600 text-xs font-normal">*</span>
            </div>
            <div className="h-9 w-[300px]">
              <NonSearchableSelectMenu
                onSelectItem={(item) => {
                  if (item) {
                    formHook.setValue("categoryId", item?.id);
                    formHook.setValue("categoryName", item?.title);
                    clearError("categoryId");
                  }
                }}
                fieldError={formHook.formState.errors.categoryId}
                register={formHook.register("categoryId", { required: true })}
                errorMessages={[{ message: "Category is required", type: "required" }]}
                selectItems={staffProviderCategoryItem}
                placeholder="Select Category"
                showTooltips={false}
                showTypedErrors={true}
                showDropdownIcon={true}
                defaultSelected={
                  staffProviderCategoryItem?.filter((oc) => oc?.id === formHook.watch("categoryId"))?.[0]
                }
              />
            </div>
          </div>
          <div className="justify-start items-center flex mt-8">
            <div className="h-[38px] p-2.5 justify-start w-[250px] items-center gap-1 flex">
              <span className="text-black text-xs font-normal">Staff Role</span>
              <span className="text-rose-600 text-xs font-normal">*</span>
            </div>
            <div className="h-9 w-[300px]">
              <NonSearchableSelectMenu
                selectItems={staffRolesItem}
                errorMessages={[{ message: "Staff Role is required", type: "required" }]}
                onSelectItem={(item) => {
                  if (item) {
                    formHook.setValue("staffRoleName", item?.title);
                    formHook.setValue("staffRoleId", item?.id);
                    clearError("staffRoleId");
                  }
                }}
                fieldError={formHook.formState.errors.staffRoleId}
                register={formHook.register("staffRoleId", { required: true })}
                placeholder="Select Staff Role"
                showTooltips={false}
                showTypedErrors={true}
                showDropdownIcon={true}
                defaultSelected={staffRolesItem?.filter((oc) => oc?.id === formHook.watch("staffRoleId"))?.[0]}
              />
            </div>
          </div>
          {isDoctor && (
            <div>
              <div className="justify-start items-baseline flex mt-5">
                <div className="h-[38px] p-2.5 justify-start w-[250px] items-center gap-1 flex">
                  <span className="text-black text-xs font-normal">Education/Qualification </span>
                  <span className="text-rose-600 text-xs font-normal">*</span>
                </div>
                <Input
                  register={formHook.register("expertise", {
                    required: isDoctor,
                  })}
                  errorMessages={[forStaffName.errors, { type: "required", message: "" }]}
                  className="h-9 w-[300px] text-xs rounded-md border-slate-400"
                  fieldError={formHook.formState.errors.expertise}
                  placeholder="Enter Education"
                />
              </div>
              <div className="justify-start items-baseline flex mt-5">
                <div className="h-[38px] p-2.5 justify-start w-[250px] items-center gap-1 flex">
                  <span className="text-black text-xs font-normal">Experience (yrs)</span>
                  <span className="text-rose-600 text-xs font-normal">*</span>
                </div>
                <Input
                  register={formHook.register("experience", {
                    required: isDoctor,
                  })}
                  errorMessages={[forStaffName.errors, { type: "required", message: "" }]}
                  className="h-9 w-[300px] text-xs rounded-md border-slate-400"
                  fieldError={formHook.formState.errors.experience}
                  placeholder="Enter Experience"
                />
              </div>
            </div>
          )}
          <div className="justify-start items-center flex mt-5">
            <div className="h-[38px] p-2.5 justify-start w-[250px] gap-1 flex">
              <span className="text-black text-xs font-normal">Choose Cities</span>
              <span className="text-rose-600 text-xs font-normal">*</span>
            </div>
            <div className="h-9 w-[300px]">
              <SearchableMultiSelectMenu
                withSelectAll
                selectItems={cityOrgItem}
                onSelectItem={(items) => {
                  if (items) {
                    formHook.setValue(
                      "cities",
                      items?.map((i) => i?.id),
                    );
                    clearError("cities");
                  }
                }}
                defaultSelected={cityOrgItem?.filter((c) => formHook?.watch("cities")?.includes(c?.id))}
                placeholder="Select Cities"
                className="text-xs"
                showDropdownIcon={true}
                errorMessages={[{ type: "required", message: "City is required." }]}
                fieldError={formHook.formState.errors.cities}
                register={formHook.register("cities", { required: true })}
              />
            </div>
          </div>
          <div className="justify-start items-center flex mt-8">
            <div className="h-[38px] p-2.5 justify-start w-[250px] items-center gap-1 flex">
              <span className="text-black text-xs font-normal">Choose Clinics</span>
              <span className="text-rose-600 text-xs font-normal">*</span>
            </div>
            <div className="h-9 w-[300px]">
              <SearchableMultiSelectMenu
                withSelectAll
                withDropDownSearch
                selectItems={clinicsOrgItem}
                onSelectItem={(items) => {
                  if (items) {
                    handleClinicMultiSelect(items);
                  }
                }}
                defaultSelected={clinicsOrgItem?.filter((c) => formHook?.watch("clinics")?.includes(c?.id))}
                placeholder="Select Clinics"
                className="text-xs"
                showDropdownIcon={true}
                errorMessages={[{ type: "required", message: "Clinic is required." }]}
                fieldError={formHook.formState.errors.clinics}
                register={formHook.register("clinics", { required: true })}
              />
            </div>
          </div>
          {formHook?.watch("clinics")?.length > 0 && (
            <div className="justify-start items-center flex mt-10">
              <div className="h-[38px] p-2.5 justify-start w-[250px] items-center gap-1 flex">
                <span className="text-black text-xs font-normal">Service Mapping</span>
              </div>
              <Toggle
                onToggle={(state: boolean) => {
                  setIsServiceMappingOn(state);
                }}
                checked={isServiceMappingOn}
              />
            </div>
          )}
          <div className="mt-4 mb-4 w-full h-full z-20">
            {isServiceMappingOn &&
              clinicServiceDetails?.length > 0 &&
              clinicServiceDetails?.map((details, index) => {
                return (
                  <div key={details?.clinic_id} className="mb-4 mt-4">
                    <FadeIn show key={index}>
                      <div className="justify-start mb-10 items-center flex mt-5">
                        <div className="h-[38px] p-2.5 justify-start gap-1 w-[15.5rem] flex">
                          <span className="text-black text-xs font-normal">
                            Choose Service For <strong>{getClinicName(details?.clinic_id)}</strong>{" "}
                          </span>
                          <span className="text-rose-600 text-xs font-normal">*</span>
                        </div>
                        <div className="h-9 w-[500px]">
                          <SelectMenuWithSubOptions
                            selectItems={eqpItems}
                            onParentItemSelection={(items) => {
                              if (items) {
                                // console.log(items, "items");
                                setClinicServiceDetails((prev) => {
                                  return prev.map((p) => {
                                    if (p?.clinic_id === details?.clinic_id) {
                                      const selectedServiceTypeIds = items.map((it) => it?.id) || [];
                                      const validSubServiceIdsForSelectedServiceTypes = getValidSubServiceIdsForServices(
                                        selectedServiceTypeIds,
                                        p?.sub_service_ids || [],
                                      );
                                      const serviceIdsWithSelectedSubServices = [
                                        ...new Set(
                                          validSubServiceIdsForSelectedServiceTypes
                                            .map((subServiceId) => getServiceIdBySubServiceId(subServiceId))
                                            .filter(Boolean),
                                        ),
                                      ] as string[];

                                      return {
                                        ...p,
                                        service_ids: serviceIdsWithSelectedSubServices,
                                        sub_service_ids: validSubServiceIdsForSelectedServiceTypes,
                                      };
                                    }
                                    return p;
                                  });
                                });
                              }
                            }}
                            onChildItemSelection={(items) => {
                              if (items) {
                                setClinicServiceDetails((prev) => {
                                  return prev.map((p) => {
                                    if (p?.clinic_id === details?.clinic_id) {
                                      const selectedSubServiceIds = items.map((it) => it?.id) || [];
                                      const serviceIdsWithSelectedSubServices = [
                                        ...new Set(
                                          selectedSubServiceIds
                                            .map((subServiceId) => getServiceIdBySubServiceId(subServiceId))
                                            .filter(Boolean),
                                        ),
                                      ] as string[];

                                      return {
                                        ...p,
                                        service_ids: serviceIdsWithSelectedSubServices,
                                        sub_service_ids: [...new Set(selectedSubServiceIds)],
                                      };
                                    }

                                    return p;
                                  });
                                });
                              }
                            }}
                            defaultSelectedChildIds={getDefaultChildIds(details?.clinic_id)}
                            defaultSelectedParentIds={getDefaultParentIds(details?.clinic_id)}
                          />
                        </div>
                        {isSubmit && (
                          <div className="text-[9px] ml-5 mt-1 text-red-500">
                            {clinicServiceDetails?.find((c) => c?.clinic_id == details?.clinic_id)?.service_ids
                              ?.length == 0
                              ? "Please Select a service"
                              : getEmptyServices(details?.clinic_id)?.length > 0
                                ? `${getEmptyServices(details?.clinic_id)?.join(",")} sub-services are required`
                                : ""}
                          </div>
                        )}
                      </div>
                    </FadeIn>
                  </div>
                );
              })}
          </div>

          <div className="justify-start items-baseline flex mt-5">
            <div className="h-[38px] p-2.5 w-[250px] flex-col gap-1 flex">
              <p className="text-black text-xs font-normal">Image Url </p>
              <p className="text-blue-600 text-xs font-normal cursor-pointer" onClick={() => handelImageRedirect()}>
                (Upload Image using this link)
              </p>
            </div>
            <Input
              register={formHook.register("image_url", {
                required: false,
              })}
              errorMessages={[forStaffName.errors, { type: "required", message: "Staff Display Name is required" }]}
              className="h-9 w-[300px] text-xs rounded-md border-slate-400"
              fieldError={formHook.formState.errors.image_url}
              placeholder="Paste Image Url"
            />
          </div>
          <div className="justify-start items-baseline flex mt-5">
            <div className="h-[38px] p-2.5 justify-start w-[250px] items-center gap-1 flex">
              <span className="text-black text-xs font-normal">Designation</span>
            </div>
            <Input
              register={formHook.register("designation", {
                required: false,
              })}
              errorMessages={[forStaffName.errors, { type: "required", message: "Staff Display Name is required" }]}
              className="h-9 w-[300px] text-xs rounded-md border-slate-400"
              fieldError={formHook.formState.errors.designation}
              placeholder="Enter Designation"
            />
          </div>
          <div className="justify-start items-baseline flex mt-5">
            <div className="h-[38px] p-2.5 justify-start w-[250px] items-center gap-1 flex">
              <span className="text-black text-xs font-normal">Specialties</span>
            </div>
            <Input
              register={formHook.register("specialities", {
                required: false,
              })}
              errorMessages={[forStaffName.errors, { type: "required", message: "Staff Display Name is required" }]}
              className="h-9 w-[300px] text-xs rounded-md border-slate-400"
              fieldError={formHook.formState.errors.specialities}
              placeholder="Enter Specialties"
            />
          </div>

          <div className="justify-start items-center flex mt-8 mb-20">
            <div className="h-[38px] p-2.5 justify-start w-[250px] items-center gap-1 flex">
              <span className="text-black text-xs font-normal">Status</span>
              <span className="text-rose-600 text-xs font-normal">*</span>
            </div>
            <div className="w-3/4 flex gap-3">
              <div className="flex justify-start items-center">
                <RadioGroupComponent
                  alignment="horizontal"
                  errorMessages={[]}
                  fieldError={formHook.formState.errors.staffRoleStatus}
                  register={formHook.register("staffRoleStatus")}
                  groupOptions={[
                    { id: "active", title: "Active" },
                    { id: "inactive", title: "In Active" },
                  ]}
                  defaultSelectedId={formHook.watch("staffRoleStatus")}
                  onRadioChange={(optionId: string) => {
                    formHook.setValue("staffRoleStatus", optionId as StaffRoleStatusTypes);
                  }}
                  radioOptionClassName="text-sm"
                />
              </div>
            </div>
          </div>
          <div className="fixed bottom-0 bg-white right-5 border-t-2 p-3 justify-end items-center gap-4 flex w-full">
            <button type="button" onClick={handleCancel} className={SECONDARY_BUTTON}>
              Cancel
            </button>
            <button type="submit" className={PRIMARY_BUTTON}>
              Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ConfigurationStaffOnboard;
